// src/hooks/useDeviceAuthFlow.ts
// ────────────────────────────────────────────────────────────────────────────
// React hook encapsulating the RFC 8628 Device Authorization Grant polling
// state machine.  Used by ScopeMigrationPanel to pair with a remote BFF.
//
// Token lifecycle (best practice — approve once, reuse forever):
//   1. First `initiate()` → full device-code flow → admin approval required.
//   2. On approval the access_token + refresh_token are cached in
//      localStorage, keyed by (origin, scope).
//   3. Subsequent `initiate()` calls detect the cached refresh_token,
//      silently exchange it for a fresh access_token (no approval needed),
//      and jump straight to `approved`.
//   4. If the refresh fails (revoked/expired after 30 d), fall back to the
//      full device-code flow automatically.
//   5. `disconnect()` explicitly clears the cache.
//
// States:  idle → requesting → awaiting_approval → polling → approved | error | expired
//
// Usage:
//   const auth = useDeviceAuthFlow();
//   auth.initiate('https://bff-dev.a.run.app', 'scopes.migrate');
//   // first time  → shows user_code → polls → approved (token cached)
//   // next time   → instant approved (silent refresh)
//   auth.disconnect();  // explicit un-pair, clears cache
// ────────────────────────────────────────────────────────────────────────────
import { useCallback, useRef, useState } from 'react';
import {
  computeBrowserFingerprint,
  getOrCreateStableDeviceId as resolveStableDeviceId,
} from '@/lib/deviceFingerprint';
import { bff } from '@/lib/http/bff';

export type DeviceAuthPhase =
  | 'idle'
  | 'requesting'
  | 'awaiting_approval'
  | 'polling'
  | 'approved'
  | 'error'
  | 'expired';

export interface DeviceAuthState {
  phase: DeviceAuthPhase;
  /** The human-readable pairing code, e.g. "ABCD-EFGH". */
  userCode: string | null;
  /** URL the admin should open to approve. */
  verificationUri: string | null;
  /** Seconds until the device code expires. */
  expiresIn: number | null;
  /** Bearer access token, available once phase === 'approved'. */
  accessToken: string | null;
  /** OAuth refresh token (for long-lived sessions). */
  refreshToken: string | null;
  /** Human-readable error. */
  error: string | null;
}

const INITIAL: DeviceAuthState = {
  phase: 'idle',
  userCode: null,
  verificationUri: null,
  expiresIn: null,
  accessToken: null,
  refreshToken: null,
  error: null,
};

const CLIENT_ID = 'pac-daemon';

// ── Stable device identity ─────────────────────────────────────────────────
// Delegated to `@/lib/deviceFingerprint` which provides:
//   1. Multi-layer persistence (IndexedDB → localStorage → sessionStorage)
//   2. Deterministic browser fingerprint fallback — same device always
//      produces the same fe-peer-<hash> even after full storage wipe.
//
// This guarantees the Device Registry references ONE stable entry per
// physical device/browser, eliminating duplicate registrations on re-auth.
// ────────────────────────────────────────────────────────────────────────────

// ── Token cache (localStorage) ──────────────────────────────────────────────
// Tokens are persisted so the user only approves once per (origin, scope)
// pair.  The refresh token has a 30-day TTL on the server; localStorage
// survives browser restarts, tab navigations, and page refreshes.
// ────────────────────────────────────────────────────────────────────────────

interface CachedTokens {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms when the access token expires (approximate). */
  accessExpiresAt: number;
  deviceId: string;
  scope: string;
  cachedAt: number;
}

function cacheKey(origin: string, scope: string): string {
  try {
    const host = new URL(origin).hostname;
    return `pac_device_auth_${host}_${scope}`;
  } catch {
    return `pac_device_auth_${btoa(origin)}_${scope}`;
  }
}

function loadCachedTokens(origin: string, scope: string): CachedTokens | null {
  try {
    const raw = localStorage.getItem(cacheKey(origin, scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedTokens;
    // Basic sanity: must have a refresh token.
    if (!parsed?.refreshToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCachedTokens(origin: string, scope: string, tokens: CachedTokens): void {
  try {
    localStorage.setItem(cacheKey(origin, scope), JSON.stringify(tokens));
  } catch {
    // Storage full or unavailable — non-fatal.
  }
}

function clearCachedTokens(origin: string, scope: string): void {
  try {
    localStorage.removeItem(cacheKey(origin, scope));
  } catch {
    // non-fatal
  }
}

/**
 * Exchange a refresh_token for a fresh access_token + rotated refresh_token.
 * Returns the token response body on success, or null on failure.
 */
async function silentRefresh(
  tokenEndpoint: string,
  refreshToken: string,
  signal?: AbortSignal,
): Promise<{
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
} | null> {
  try {
    const res = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: CLIENT_ID,
      }),
      signal,
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * RFC 8628 Device Authorization Grant state-machine hook.
 *
 * Call `initiate(remoteBffOrigin, scope)` to start the flow.
 * The hook exposes reactive `phase`, `userCode`, `verificationUri`,
 * `accessToken`, and `error` fields.
 *
 * Tokens are cached in localStorage so approval is only required once per
 * (origin, scope) pair.  Subsequent calls silently refresh the access token.
 */
export function useDeviceAuthFlow() {
  const [state, setState] = useState<DeviceAuthState>(INITIAL);

  // We keep a mutable ref so we can abort polling on cancel/unmount.
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track the current session coordinates for disconnect / refreshAccessToken.
  const originRef = useRef<string>('');
  const scopeRef = useRef<string>('');
  const basePathRef = useRef<string>('');

  /** Clean up any in-flight polling. */
  const cleanup = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /** Cancel an in-flight flow and reset to idle. */
  const cancel = useCallback(() => {
    cleanup();
    setState(INITIAL);
  }, [cleanup]);

  /** Reset to idle — keeps cache so next initiate() is instant. */
  const reset = useCallback(() => {
    cleanup();
    setState(INITIAL);
  }, [cleanup]);

  /** Explicitly disconnect: clears cached tokens so the next initiate()
   *  will require a full device-code approval. */
  const disconnect = useCallback(() => {
    cleanup();
    if (originRef.current && scopeRef.current) {
      clearCachedTokens(originRef.current, scopeRef.current);
    }
    originRef.current = '';
    scopeRef.current = '';
    basePathRef.current = '';
    setState(INITIAL);
  }, [cleanup]);

  /**
   * Silently refresh the access token using the cached refresh token.
   * Returns the new access token on success, or null if refresh failed
   * (caller should fall back to full re-auth).
   */
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const origin = originRef.current;
    const scope = scopeRef.current;
    const bp = basePathRef.current;
    if (!origin) return null;

    const cached = loadCachedTokens(origin, scope);
    const rt = cached?.refreshToken;
    if (!rt) return null;

    const base = `${origin.replace(/\/+$/, '')}${bp}`;
    const body = await silentRefresh(`${base}/oauth/token`, rt);
    if (!body) {
      // Refresh token is dead — clear cache.
      clearCachedTokens(origin, scope);
      return null;
    }

    // Persist rotated tokens.
    saveCachedTokens(origin, scope, {
      accessToken: body.access_token,
      refreshToken: body.refresh_token ?? rt,
      accessExpiresAt: Date.now() + (body.expires_in ?? 600) * 1000,
      deviceId: cached?.deviceId ?? '',
      scope,
      cachedAt: Date.now(),
    });

    setState((s) => ({
      ...s,
      phase: 'approved',
      accessToken: body.access_token,
      refreshToken: body.refresh_token ?? s.refreshToken,
      error: null,
    }));

    return body.access_token;
  }, []);

  /**
   * Check whether a cached session exists for the given (origin, scope).
   * Useful for UI hints like "Previously paired".
   */
  const hasCachedSession = useCallback(
    (origin: string, scope: string): boolean => loadCachedTokens(origin, scope) !== null,
    [],
  );

  /**
   * Kick off the device auth flow against a remote BFF.
   *
   * If a cached refresh token exists for (origin, scope), the hook silently
   * refreshes the access token and jumps straight to `approved` — no
   * user_code, no manual approval.  Only falls back to the full device-code
   * flow when the cache is empty or the refresh token is expired/revoked.
   *
   * @param remoteBffOrigin  Full origin, e.g. `https://bff-dev.a.run.app`
   * @param scope            Requested scope, e.g. `scopes.migrate`
   * @param basePath         BFF base path (device OAuth lives at root, not under /bff)
   */
  const initiate = useCallback(
    async (remoteBffOrigin: string, scope: string, basePath = '') => {
      cleanup();

      // Remember session coordinates for refresh / disconnect.
      originRef.current = remoteBffOrigin;
      scopeRef.current = scope;
      basePathRef.current = basePath;

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const base = `${remoteBffOrigin.replace(/\/+$/, '')}${basePath}`;

      // ── 0. Try cached refresh token (silent, no approval) ───────
      setState({ ...INITIAL, phase: 'requesting' });

      const cached = loadCachedTokens(remoteBffOrigin, scope);
      if (cached?.refreshToken) {
        const body = await silentRefresh(`${base}/oauth/token`, cached.refreshToken, ctrl.signal);
        if (body && !ctrl.signal.aborted) {
          // Success — update cache with rotated tokens.
          saveCachedTokens(remoteBffOrigin, scope, {
            accessToken: body.access_token,
            refreshToken: body.refresh_token ?? cached.refreshToken,
            accessExpiresAt: Date.now() + (body.expires_in ?? 600) * 1000,
            deviceId: cached.deviceId,
            scope,
            cachedAt: Date.now(),
          });

          setState({
            ...INITIAL,
            phase: 'approved',
            accessToken: body.access_token,
            refreshToken: body.refresh_token ?? cached.refreshToken,
          });
          return; // ← No device-code flow needed.
        }

        // Refresh failed — evict stale cache and fall through.
        clearCachedTokens(remoteBffOrigin, scope);
        if (ctrl.signal.aborted) return;
      }

      // Stable device ID — needed by session-token and device-code paths.
      const deviceId = await resolveStableDeviceId();
      if (ctrl.signal.aborted) return;

      // ── 0.5. Session-based direct token (same-origin fast path) ──
      // If the user already has an active admin session on this BFF,
      // POST /bff/device/token mints tokens immediately — no device code,
      // no user_code, no polling, no manual approval.  The session cookie
      // is sent automatically when the request goes through the CRA dev
      // proxy (same origin) or to the production BFF (same domain).
      //
      // Falls through to the full RFC 8628 flow for cross-origin targets
      // (remote BFFs where the user has no session).
      const isSameOrigin =
        remoteBffOrigin === window.location.origin ||
        (window.location.hostname === 'localhost' &&
          (() => {
            try {
              return new URL(remoteBffOrigin).hostname === 'localhost';
            } catch {
              return false;
            }
          })());

      if (isSameOrigin) {
        try {
          const body = await bff.post<{
            access_token: string;
            refresh_token?: string;
            expires_in?: number;
            scope?: string;
          }>('/device/token', { device_id: deviceId, scope });

          if (body?.access_token) {
            saveCachedTokens(remoteBffOrigin, scope, {
              accessToken: body.access_token,
              refreshToken: body.refresh_token ?? '',
              accessExpiresAt: Date.now() + (body.expires_in ?? 600) * 1000,
              deviceId,
              scope,
              cachedAt: Date.now(),
            });
            setState({
              ...INITIAL,
              phase: 'approved',
              accessToken: body.access_token,
              refreshToken: body.refresh_token ?? null,
            });
            return; // ← Instant approval via session — no device-code flow.
          }
        } catch {
          // Session not available, CSRF issue, or config error → fall through.
        }
      }

      // ── 1. Request device code (full flow) ─────────────────────

      // Compute fingerprint separately for server-side dedup correlation.
      const fingerprint = await computeBrowserFingerprint();

      // Collect browser/device metadata for the device registry.
      // This travels through the OAuth store and gets persisted as scope-entity
      // attributes when an admin approves the device.
      const deviceMeta = {
        user_agent:  navigator.userAgent,
        origin:      window.location.origin,
        platform:    navigator.platform ?? (navigator as any).userAgentData?.platform ?? '',
        language:    navigator.language,
        // eslint-disable-next-line no-restricted-globals
        screen:      `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
        fingerprint,
      };

      let codeRes: any;
      try {
        const res = await fetch(`${base}/oauth/device/code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: CLIENT_ID,
            scope,
            device_id: deviceId,
            metadata: deviceMeta,
          }),
          signal: ctrl.signal,
        });
        codeRes = await res.json();
        if (!res.ok) {
          setState({
            ...INITIAL,
            phase: 'error',
            error: codeRes?.error_description || codeRes?.error || `HTTP ${res.status}`,
          });
          return;
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setState({
          ...INITIAL,
          phase: 'error',
          error: err?.message ?? String(err),
        });
        return;
      }

      const {
        user_code,
        verification_uri,
        device_code,
        expires_in,
        interval: pollIntervalSec,
      } = codeRes;

      setState({
        ...INITIAL,
        phase: 'awaiting_approval',
        userCode: user_code,
        verificationUri: verification_uri,
        expiresIn: expires_in,
      });

      // ── 2. Poll for approval ────────────────────────────────────
      let intervalMs = (pollIntervalSec ?? 5) * 1000;
      // C3: Client-side minimum poll interval (RFC 8628 §3.5 compliance).
      // Never poll faster than the server-advertised interval, even if
      // something resets intervalMs accidentally.
      const minIntervalMs = (pollIntervalSec ?? 5) * 1000;
      const deadline = Date.now() + (expires_in ?? 600) * 1000;
      let lastPollAt = 0;

      const poll = async () => {
        if (ctrl.signal.aborted) return;
        if (Date.now() >= deadline) {
          setState((s) => ({ ...s, phase: 'expired', error: 'Device code expired' }));
          return;
        }

        // C3: Enforce minimum interval between polls (defense in depth).
        const now = Date.now();
        const elapsed = now - lastPollAt;
        if (lastPollAt > 0 && elapsed < minIntervalMs) {
          const wait = minIntervalMs - elapsed;
          timerRef.current = setTimeout(poll, wait);
          return;
        }
        lastPollAt = Date.now();

        setState((s) => (s.phase === 'awaiting_approval' || s.phase === 'polling' ? { ...s, phase: 'polling' } : s));

        try {
          const res = await fetch(`${base}/oauth/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
              device_code,
              client_id: CLIENT_ID,
            }),
            signal: ctrl.signal,
          });

          const body = await res.json();

          if (res.ok) {
            // Approved — cache tokens for next time.
            saveCachedTokens(remoteBffOrigin, scope, {
              accessToken: body.access_token,
              refreshToken: body.refresh_token ?? '',
              accessExpiresAt: Date.now() + (body.expires_in ?? 600) * 1000,
              deviceId,
              scope,
              cachedAt: Date.now(),
            });

            setState((s) => ({
              ...s,
              phase: 'approved',
              accessToken: body.access_token,
              refreshToken: body.refresh_token ?? null,
              error: null,
            }));
            return;
          }

          const oauthError = body?.error;

          if (oauthError === 'authorization_pending') {
            // Keep polling.
            timerRef.current = setTimeout(poll, intervalMs);
            return;
          }

          if (oauthError === 'slow_down') {
            // RFC 8628 §3.5: increase interval by 5 s.
            intervalMs += 5_000;
            timerRef.current = setTimeout(poll, intervalMs);
            return;
          }

          if (oauthError === 'expired_token') {
            setState((s) => ({ ...s, phase: 'expired', error: 'Device code expired' }));
            return;
          }

          // Any other error is terminal.
          setState((s) => ({
            ...s,
            phase: 'error',
            error: body?.error_description || oauthError || `HTTP ${res.status}`,
          }));
        } catch (err: any) {
          if (err?.name === 'AbortError') return;
          // Network blip — retry after a larger interval.
          timerRef.current = setTimeout(poll, intervalMs * 2);
        }
      };

      // First poll after interval.
      timerRef.current = setTimeout(poll, intervalMs);
    },
    [cleanup],
  );

  return {
    ...state,
    initiate,
    cancel,
    reset,
    /** Explicitly disconnect — clears cached tokens. Next initiate() will
     *  require full device-code approval again. */
    disconnect,
    /** Silently refresh the access token. Returns new token or null. */
    refreshAccessToken,
    /** Check if a cached session exists (UI hint). */
    hasCachedSession,
  };
}

export type DeviceAuthFlowHook = ReturnType<typeof useDeviceAuthFlow>;
